import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { Shell } from "../app/Shell";
import { useAuth } from "./AuthProvider";
import type { Capability } from "./capabilities";

/** Where an account holding a password somebody else chose has to go first. */
export const CHANGE_PASSWORD_PATH = "/change-password";

/** Gate for every authenticated screen.
 *
 * While the session is being restored nothing is rendered and nothing is
 * redirected: flashing the Login page at an administrator who is in fact signed
 * in, and then bouncing them back, is worse than a moment of blankness.
 *
 * An account that has not yet chosen its own password is sent to the one screen
 * that can end that, because every other screen it could reach would answer
 * `password_change_required` and show it an error instead of a way out. THE
 * REDIRECT IS NOT THE CONTROL — the backend refuses those routes whatever the
 * browser does — it is what stops the operator being shown a wall.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, mustChangePassword } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="empty" role="status">
        Restoring session…
      </div>
    );
  }

  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (mustChangePassword && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  return <>{children}</>;
}

/** A screen an administrator's capabilities do not reach.
 *
 * Rendered rather than redirected: bouncing somebody who typed an address they
 * are not permitted to visit leaves them somewhere they did not ask for, with
 * no explanation of why. This says the true thing in the place they asked for.
 *
 * IT IS NOT A SECURITY BOUNDARY and does not pretend to be one. The data behind
 * the route is refused by the API; this only avoids rendering a screen whose
 * every request would fail.
 */
export function RequireCapability({
  capability,
  title,
  children,
}: {
  capability: Capability;
  title: string;
  children: ReactNode;
}) {
  const { can } = useAuth();
  if (can(capability)) return <>{children}</>;

  return (
    <Shell title={title}>
      <div className="panel">
        <div className="empty" role="status">
          You don&apos;t have permission to view this section. Ask an owner if
          you need access.
        </div>
      </div>
    </Shell>
  );
}
